import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  UserPlus, UserMinus, UserCog, FileText, Trash2, ArrowRightLeft,
  ClipboardList, KeyRound, ChevronLeft, ChevronRight, Search, Filter, Clock,
  MessageCircle, Send, Calendar as CalendarIcon, GitMerge, RefreshCw, Mail,
  Activity, CreditCard, TrendingDown, UserPlus2, Upload, Eye, Layers, X, RotateCcw,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Comprehensive action map covering all action types currently logged.
// Each entry: { label, icon, color (badge bg+text), group }
const actionConfig = {
  // Lead lifecycle
  lead_created:           { label: "Lead Created",           icon: FileText,       color: "bg-emerald-100 text-emerald-700", group: "Leads" },
  lead_updated:           { label: "Lead Updated",           icon: ClipboardList,  color: "bg-blue-100 text-blue-700", group: "Leads" },
  lead_status_changed:    { label: "Status Changed",         icon: ArrowRightLeft, color: "bg-purple-100 text-purple-700", group: "Leads" },
  lead_assigned:          { label: "Lead Assigned",          icon: UserPlus2,      color: "bg-indigo-100 text-indigo-700", group: "Leads" },
  lead_deleted:           { label: "Lead Deleted",           icon: Trash2,         color: "bg-red-100 text-red-700", group: "Leads" },
  leads_bulk_imported:    { label: "Leads Bulk Imported",    icon: Upload,         color: "bg-cyan-100 text-cyan-700", group: "Leads" },
  // Follow-ups & meetings
  lead_followup_recorded: { label: "Follow-up Done",         icon: Activity,       color: "bg-teal-100 text-teal-700", group: "Follow-ups" },
  lead_followup_rescheduled: { label: "Follow-up Rescheduled", icon: RefreshCw,    color: "bg-amber-100 text-amber-700", group: "Follow-ups" },
  meeting_scheduled:      { label: "Meeting Scheduled",      icon: CalendarIcon,   color: "bg-violet-100 text-violet-700", group: "Follow-ups" },
  teams_invite_sent:      { label: "Teams Invite Sent",      icon: Send,           color: "bg-sky-100 text-sky-700", group: "Follow-ups" },
  // Merging & duplicates
  engarage_merged:        { label: "ENGARAGE Merged",        icon: GitMerge,       color: "bg-fuchsia-100 text-fuchsia-700", group: "ENGARAGE" },
  // ENGARAGE subscription / usage
  engarage_registered:    { label: "ENGARAGE Registered",    icon: UserPlus,       color: "bg-emerald-100 text-emerald-700", group: "ENGARAGE" },
  engarage_subscribed:    { label: "ENGARAGE Subscribed",    icon: CreditCard,     color: "bg-green-100 text-green-700", group: "ENGARAGE" },
  engarage_downgraded:    { label: "ENGARAGE Downgraded",    icon: TrendingDown,   color: "bg-red-100 text-red-700", group: "ENGARAGE" },
  engarage_usage_updated: { label: "ENGARAGE Usage Updated", icon: Activity,       color: "bg-amber-100 text-amber-700", group: "ENGARAGE" },
  // WhatsApp
  whatsapp_sent:          { label: "WhatsApp Sent",          icon: MessageCircle,  color: "bg-green-100 text-green-700", group: "Communications" },
  whatsapp_delivered:     { label: "WhatsApp Delivered",     icon: MessageCircle,  color: "bg-emerald-100 text-emerald-700", group: "Communications" },
  whatsapp_read:          { label: "WhatsApp Read",          icon: Eye,            color: "bg-sky-100 text-sky-700", group: "Communications" },
  whatsapp_failed:        { label: "WhatsApp Failed",        icon: X,              color: "bg-red-100 text-red-700", group: "Communications" },
  whatsapp_preview_sent:  { label: "WhatsApp Preview",       icon: Send,           color: "bg-cyan-100 text-cyan-700", group: "Communications" },
  whatsapp_followup_failed: { label: "WhatsApp Follow-up Failed", icon: X,         color: "bg-red-100 text-red-700", group: "Communications" },
  whatsapp_followup_sent: { label: "WhatsApp Follow-up Sent", icon: MessageCircle, color: "bg-green-100 text-green-700", group: "Communications" },
  daily_summary_sent:     { label: "Daily Summary Sent",     icon: Mail,           color: "bg-blue-100 text-blue-700", group: "Communications" },
  followup_reminders_sent:{ label: "Follow-up Reminders",    icon: Mail,           color: "bg-blue-100 text-blue-700", group: "Communications" },
  // Team / users
  team_member_added:      { label: "Member Added",           icon: UserPlus,       color: "bg-emerald-100 text-emerald-700", group: "Team" },
  team_member_updated:    { label: "Member Updated",         icon: UserCog,        color: "bg-blue-100 text-blue-700", group: "Team" },
  team_member_deleted:    { label: "Member Removed",         icon: UserMinus,      color: "bg-red-100 text-red-700", group: "Team" },
  password_reset_by_admin:{ label: "Password Reset (Admin)", icon: KeyRound,       color: "bg-orange-100 text-orange-700", group: "Team" },
  password_reset:         { label: "Password Reset",         icon: KeyRound,       color: "bg-orange-100 text-orange-700", group: "Team" },
  password_changed:       { label: "Password Changed",       icon: KeyRound,       color: "bg-orange-100 text-orange-700", group: "Team" },
};

const formatActionLabel = (action) => {
  if (actionConfig[action]) return actionConfig[action].label;
  return action.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
};

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const ENTITY_LABELS = { lead: "Leads", user: "Team", system: "System", integration: "Integration" };

function ChangeDetails({ details }) {
  if (!details || Object.keys(details).length === 0) return null;
  const entries = Object.entries(details).filter(([k]) => k !== "custom_fields");
  if (entries.length === 0) return null;
  const fmtVal = (val) => {
    if (val == null) return "—";
    if (typeof val === "boolean") return val ? "Yes" : "No";
    if (typeof val === "object") return JSON.stringify(val);
    if (typeof val === "string" && val.length > 80) return val.slice(0, 80) + "…";
    return String(val);
  };
  return (
    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
      {entries.map(([key, val]) => {
        const label = key.replace(/_/g, " ");
        if (val && typeof val === "object" && "old" in val && "new" in val) {
          return (
            <div key={key} className="flex items-center gap-1 text-gray-600 min-w-0">
              <span className="font-semibold text-gray-700 capitalize shrink-0">{label}:</span>
              <span className="line-through text-red-500 truncate">{fmtVal(val.old)}</span>
              <ArrowRightLeft className="w-3 h-3 text-gray-300 shrink-0" />
              <span className="text-emerald-600 truncate">{fmtVal(val.new)}</span>
            </div>
          );
        }
        return (
          <div key={key} className="text-gray-600 min-w-0 truncate">
            <span className="font-semibold text-gray-700 capitalize">{label}:</span> <span className="text-gray-500">{fmtVal(val)}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  // Filters
  const [actionFilter, setActionFilter] = useState("all");
  const [entityFilter, setEntityFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  // Facets
  const [facets, setFacets] = useState({ actions: [], users: [], entities: [] });

  const fetchFacets = async () => {
    try {
      const { data } = await axios.get(`${API}/audit-logs/facets`);
      setFacets(data || { actions: [], users: [], entities: [] });
    } catch {}
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      if (actionFilter !== "all") params.set("action", actionFilter);
      if (entityFilter !== "all") params.set("entity_type", entityFilter);
      if (userFilter !== "all") params.set("user_name", userFilter);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (searchTerm.trim()) params.set("search", searchTerm.trim());
      const { data } = await axios.get(`${API}/audit-logs?${params}`);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch {
      setLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFacets(); }, []);

  // Refetch with debounce for search (others apply immediately)
  useEffect(() => {
    const t = setTimeout(() => { fetchLogs(); }, 250);
    return () => clearTimeout(t);
  }, [page, pageSize, actionFilter, entityFilter, userFilter, dateFrom, dateTo, searchTerm]); // eslint-disable-line

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [actionFilter, entityFilter, userFilter, dateFrom, dateTo, searchTerm, pageSize]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const hasFilters = actionFilter !== "all" || entityFilter !== "all" || userFilter !== "all" || dateFrom || dateTo || searchTerm.trim();
  const clearFilters = () => {
    setActionFilter("all"); setEntityFilter("all"); setUserFilter("all");
    setDateFrom(""); setDateTo(""); setSearchTerm("");
  };

  // Quick filter chips per group (one-click to filter all actions in that group)
  const groupedActions = useMemo(() => {
    const m = {};
    Object.entries(actionConfig).forEach(([key, cfg]) => {
      if (!m[cfg.group]) m[cfg.group] = [];
      m[cfg.group].push(key);
    });
    return m;
  }, []);

  const setActionMultiSelect = (actions) => {
    setActionFilter(actions.join(","));
  };

  // Group logs by date for visual separation
  const groupedByDate = useMemo(() => {
    const groups = {};
    logs.forEach(l => {
      try {
        const d = format(new Date(l.created_at), "yyyy-MM-dd");
        if (!groups[d]) groups[d] = [];
        groups[d].push(l);
      } catch {}
    });
    return groups;
  }, [logs]);

  const dateKeys = Object.keys(groupedByDate).sort((a, b) => b.localeCompare(a));

  // Pagination range builder
  const renderPageNumbers = () => {
    const max = 5;
    let start = Math.max(1, page - Math.floor(max / 2));
    let end = Math.min(totalPages, start + max - 1);
    if (end - start + 1 < max) start = Math.max(1, end - max + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto animate-fade-in" data-testid="audit-log-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Audit Log</h1>
          <p className="text-sm text-gray-500 mt-1">Track every change across leads, team, and integrations</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-sm px-3 py-1">
            {total.toLocaleString()} {total === 1 ? "entry" : "entries"}
          </Badge>
          <Button variant="outline" size="sm" onClick={() => { fetchLogs(); fetchFacets(); }} className="border-gray-300 rounded-lg" data-testid="audit-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Quick chips by group */}
      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        <span className="inline-flex items-center text-[10px] uppercase tracking-wider text-gray-400 font-semibold gap-1 mr-1">
          <Layers className="w-3 h-3" /> Quick filters:
        </span>
        <button
          onClick={() => setActionFilter("all")}
          className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${actionFilter === "all" ? "bg-[#0D0D5B] text-white border-[#0D0D5B]" : "bg-white text-gray-600 border-gray-200 hover:border-[#0D0D5B]/40"}`}
          data-testid="audit-quick-all"
        >
          All
        </button>
        {Object.entries(groupedActions).map(([group, actions]) => {
          const isActive = actionFilter.split(",").sort().join(",") === actions.slice().sort().join(",");
          return (
            <button
              key={group}
              onClick={() => setActionMultiSelect(actions)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${isActive ? "bg-[#0D0D5B] text-white border-[#0D0D5B]" : "bg-white text-gray-600 border-gray-200 hover:border-[#0D0D5B]/40"}`}
              data-testid={`audit-quick-${group.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              {group}
            </button>
          );
        })}
      </div>

      {/* Filter bar */}
      <Card className="border border-gray-200 rounded-xl bg-white mb-4">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search lead, user, or action…"
                className="pl-9 pr-9 border-gray-300 rounded-lg h-9"
                data-testid="audit-search"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700" data-testid="audit-clear-search">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <Select value={entityFilter} onValueChange={setEntityFilter}>
              <SelectTrigger className="w-36 h-9 border-gray-300 rounded-lg" data-testid="audit-entity-filter">
                <Filter className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                <SelectValue placeholder="Entity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                {(facets.entities || []).map(e => (
                  <SelectItem key={e.value} value={e.value}>
                    {ENTITY_LABELS[e.value] || e.value} <span className="text-gray-400 ml-1">({e.count})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-52 h-9 border-gray-300 rounded-lg" data-testid="audit-action-filter">
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent className="max-h-[400px]">
                <SelectItem value="all">All Actions</SelectItem>
                {(facets.actions || []).map(a => (
                  <SelectItem key={a.value} value={a.value}>
                    {formatActionLabel(a.value)} <span className="text-gray-400 ml-1">({a.count})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={userFilter} onValueChange={setUserFilter}>
              <SelectTrigger className="w-44 h-9 border-gray-300 rounded-lg" data-testid="audit-user-filter">
                <SelectValue placeholder="User" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                {(facets.users || []).map(u => (
                  <SelectItem key={u.value} value={u.value}>
                    {u.value} <span className="text-gray-400 ml-1">({u.count})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-1.5">
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 text-xs w-36 border-gray-300 rounded-lg" data-testid="audit-date-from" />
              <span className="text-xs text-gray-400">→</span>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 text-xs w-36 border-gray-300 rounded-lg" data-testid="audit-date-to" />
            </div>

            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-500 hover:text-gray-700 h-9 px-2" data-testid="audit-clear-filters">
                <RotateCcw className="w-3 h-3 mr-1" /> Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Log list */}
      <Card className="border border-gray-200 rounded-xl bg-white overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-2">
              {[1, 2, 3, 4, 5, 6, 7, 8].map(i => <div key={i} className="h-14 bg-gray-50 rounded-lg animate-pulse" />)}
            </div>
          ) : logs.length === 0 ? (
            <div className="p-16 text-center">
              <Clock className="w-10 h-10 mx-auto text-gray-300 mb-3" />
              <p className="text-sm text-gray-500">No audit log entries match your filters</p>
              {hasFilters && (
                <Button variant="link" size="sm" onClick={clearFilters} className="text-[#0D0D5B] mt-2" data-testid="audit-empty-clear">
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <div data-testid="audit-list">
              {dateKeys.map(dateKey => {
                let dateLabel = dateKey;
                try {
                  const d = new Date(dateKey);
                  const today = format(new Date(), "yyyy-MM-dd");
                  const yesterday = format(new Date(Date.now() - 86400000), "yyyy-MM-dd");
                  if (dateKey === today) dateLabel = "Today";
                  else if (dateKey === yesterday) dateLabel = "Yesterday";
                  else dateLabel = format(d, "EEEE, d MMMM yyyy");
                } catch {}
                return (
                  <div key={dateKey}>
                    <div className="sticky top-0 z-10 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 px-5 py-2 flex items-center gap-2">
                      <CalendarIcon className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-600">{dateLabel}</span>
                      <span className="text-[11px] text-gray-400">· {groupedByDate[dateKey].length} {groupedByDate[dateKey].length === 1 ? "entry" : "entries"}</span>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {groupedByDate[dateKey].map(log => {
                        const cfg = actionConfig[log.action] || { label: formatActionLabel(log.action), icon: ClipboardList, color: "bg-gray-100 text-gray-700" };
                        const Icon = cfg.icon;
                        let timeAgo = "";
                        let timeAbs = "";
                        try {
                          const d = new Date(log.created_at);
                          timeAgo = formatDistanceToNow(d, { addSuffix: true });
                          timeAbs = format(d, "h:mm:ss a");
                        } catch {}
                        return (
                          <div key={log.id} className="px-5 py-3 hover:bg-gray-50/70 transition-colors group" data-testid={`audit-entry-${log.id}`}>
                            <div className="flex items-start gap-3">
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${cfg.color}`}>
                                <Icon className="w-4 h-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Badge className={`text-[10px] px-1.5 py-0 font-semibold ${cfg.color} border-transparent`}>{cfg.label}</Badge>
                                  <span className="text-sm font-semibold text-gray-900 truncate" data-testid={`audit-entity-${log.id}`}>{log.entity_name || log.action}</span>
                                  {log.entity_type && (
                                    <span className="text-[10px] text-gray-400 uppercase tracking-wide font-medium">{ENTITY_LABELS[log.entity_type] || log.entity_type}</span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500 mt-1 flex items-center gap-1.5 flex-wrap">
                                  <span>by <span className="font-semibold text-gray-700">{log.user_name || "System"}</span></span>
                                  <span className="text-gray-300">·</span>
                                  <span title={timeAbs} className="cursor-help">{timeAgo}</span>
                                  <span className="text-gray-300">·</span>
                                  <span className="text-gray-400 tabular-nums">{timeAbs}</span>
                                </div>
                                <ChangeDetails details={log.details} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination + page-size */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 gap-3 px-2">
        <div className="flex items-center gap-3 text-sm text-gray-600">
          <span>Showing <span className="font-semibold text-gray-900">{logs.length === 0 ? 0 : ((page - 1) * pageSize + 1).toLocaleString()}</span>–<span className="font-semibold text-gray-900">{Math.min(page * pageSize, total).toLocaleString()}</span> of <span className="font-semibold text-gray-900">{total.toLocaleString()}</span></span>
          <span className="text-gray-300">|</span>
          <span className="inline-flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Rows per page</span>
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger className="h-8 w-20 border-gray-300 rounded-md text-xs" data-testid="audit-page-size">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </span>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => setPage(1)} disabled={page <= 1} className="border-gray-300 h-8 px-2.5 rounded-lg text-xs" data-testid="audit-first">First</Button>
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="border-gray-300 h-8 w-8 p-0 rounded-lg" data-testid="audit-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {renderPageNumbers().map(n => (
              <Button
                key={n}
                variant={page === n ? "default" : "outline"}
                size="sm"
                onClick={() => setPage(n)}
                className={`h-8 w-8 p-0 rounded-lg text-xs ${page === n ? "bg-[#0D0D5B] text-white hover:bg-[#1a1a7a]" : "border-gray-300"}`}
                data-testid={`audit-page-${n}`}
              >
                {n}
              </Button>
            ))}
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="border-gray-300 h-8 w-8 p-0 rounded-lg" data-testid="audit-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(totalPages)} disabled={page >= totalPages} className="border-gray-300 h-8 px-2.5 rounded-lg text-xs" data-testid="audit-last">Last</Button>
          </div>
        )}
      </div>
    </div>
  );
}
